import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFactoryList } from 'api/deviceApi';
import { factoryKeys } from 'api/quires/queryKeys';
import { LotoOrderSearchRequest } from 'api/searchRequest/LotoOrderSearchRequest';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { isSuperAdmin } from 'util/commonUtil';
import { decryptId, decryptString } from 'util/encryptUtil';

type LotoSearchBoxProps = {
  searchRequest: LotoOrderSearchRequest;
  isLoading: boolean;
  onSubmit: (req: LotoOrderSearchRequest) => void;
};

export default function LotoSearchBox({
  searchRequest,
  isLoading,
  onSubmit,
}: LotoSearchBoxProps) {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userInfoState);
  const role = decryptString(userInfo.role);
  const isSuper = isSuperAdmin(role, decryptId(userInfo.factoryId));

  const {
    data,
    isLoading: isFactoryLoading,
    refetch,
  } = useQuery(
    factoryKeys.detail(searchRequest.localId),
    () => getFactoryList(searchRequest.localId),
    {
      enabled: isSuper,
      cacheTime: 600000,
      staleTime: 600000,
    },
  );

  const { register, setValue, handleSubmit } = useForm<LotoOrderSearchRequest>({
    defaultValues: {
      factoryId: searchRequest.factoryId,
      startDate: searchRequest.startDate,
      endDate: searchRequest.endDate,
      equipName: searchRequest.equipName,
    },
  });

  useEffect(() => {
    if (!data || isFactoryLoading) {
      return;
    }

    setValue('factoryId', searchRequest.factoryId);
  }, [data]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={'filter-wrap'}>
        <div className={'filter'}>
          {isSuper && (
            <dl>
              <dt>{t('home.plant')}</dt>
              <dd className={'mg30r'}>
                <select {...register('factoryId')}>
                  <option value={''}>{t('orderManagement.all')}</option>
                  {data?.list?.map((factory) => (
                    <option value={factory.id} key={factory.id}>
                      {factory.name}
                    </option>
                  ))}
                </select>
              </dd>
            </dl>
          )}
          <dl>
            <dt>{t('conclusion.inquiryDate')}</dt>
            <dd>
              <input type={'date'} id={'startDt'} {...register('startDate')} />
              {'~'}
              <input type={'date'} id={'endDt'} {...register('endDate')} />
            </dd>
          </dl>
        </div>
        <div className={'filter'}>
          <dl>
            <dt>{t('device.deviceName')}</dt>
            <dd className={'mg30r'}>
              <input type={'text'} {...register('equipName')} />
            </dd>
            <dt>{t('orderManagement.orderTitle')}</dt>
            <dd>
              <input type={'text'} {...register('searchKey')} />
            </dd>
          </dl>
        </div>
        <button
          type={'submit'}
          className={'black_white_btn btn_search'}
          disabled={isLoading}
        >
          {t('button.search')}
        </button>
      </div>
    </form>
  );
}
